export interface GprmcResult {
  lat: number
  lon: number
  dateTime: Date
}

/**
 * Scan the first chunkSize bytes of a binary file for the first valid $GPRMC NMEA sentence.
 * Returns null if no valid fix is found or the sentence status is 'V' (void/no fix).
 *
 * Used by BlackVue and VIOFO extractors, both of which embed NMEA strings in MP4 files.
 */
export async function scanFileForGprmc(
  file: File,
  chunkSize = 131072 // 128 KB — enough for most dashcam file headers
): Promise<GprmcResult | null> {
  const blob = file.slice(0, chunkSize)
  const buffer = await blob.arrayBuffer()
  // Latin-1 decode preserves byte values as-is; NMEA sentences are ASCII
  const text = new TextDecoder('latin1').decode(buffer)

  // Match the first $GPRMC sentence (permissive: stops at \r, \n, or end)
  const match = text.match(/\$GPRMC,([^\r\n*]+)\*[0-9A-Fa-f]{2}/)
  if (!match) return null

  return parseGprmc(`$GPRMC,${match[1]}`)
}

function parseGprmc(sentence: string): GprmcResult | null {
  const parts = sentence.split(',')
  // parts[0]=$GPRMC, [1]=time, [2]=status, [3]=lat, [4]=N/S, [5]=lon, [6]=E/W, [9]=date
  if (parts.length < 10) return null
  if (parts[2] !== 'A') return null // V = void, no fix

  const lat = nmeaToDecimal(parts[3], parts[4])
  const lon = nmeaToDecimal(parts[5], parts[6])
  if (lat === null || lon === null) return null

  const dateTime = parseNmeaDateTime(parts[1], parts[9])
  if (!dateTime) return null

  return { lat, lon, dateTime }
}

function nmeaToDecimal(value: string, direction: string): number | null {
  const raw = parseFloat(value)
  if (isNaN(raw)) return null
  const degrees = Math.floor(raw / 100)
  const minutes = raw % 100
  const decimal = degrees + minutes / 60
  return direction === 'S' || direction === 'W' ? -decimal : decimal
}

function parseNmeaDateTime(timeStr: string, dateStr: string): Date | null {
  // timeStr: HHMMSS or HHMMSS.ss
  // dateStr: DDMMYY
  if (!timeStr || !dateStr || timeStr.length < 6 || dateStr.length < 6) return null

  const hours = parseInt(timeStr.slice(0, 2), 10)
  const minutes = parseInt(timeStr.slice(2, 4), 10)
  const seconds = parseInt(timeStr.slice(4, 6), 10)
  const day = parseInt(dateStr.slice(0, 2), 10)
  const month = parseInt(dateStr.slice(2, 4), 10) - 1
  const year = 2000 + parseInt(dateStr.slice(4, 6), 10)

  const d = new Date(Date.UTC(year, month, day, hours, minutes, seconds))
  return isNaN(d.getTime()) ? null : d
}
